import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { Bell, Search, ChevronDown, User, Settings, LogOut } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useUIStore } from '../../store/uiStore'
import { programmeLabel } from '../../utils/programme'

// Keyed by programme because the profile crumb carries the user's own degree.
const breadcrumbsFor = programme => ({
  '/app/dashboard':                ['Dashboard'],
  '/app/profile':                  [`${programmeLabel(programme)} Profile`],
  '/app/documents/sop':            ['Documents', 'Statement of Purpose'],
  '/app/documents/synopsis':       ['Documents', 'Synopsis'],
  '/app/documents/research-paper': ['Documents', 'Research Paper'],
  '/app/thesis':                   ['Thesis'],
  '/app/research':                 ['Research Assistant'],
  '/app/references':               ['References'],
  '/app/files':                    ['My Files'],
  '/app/billing':                  ['Billing'],
  '/app/settings':                 ['Settings'],
  '/admin':                        ['Admin Panel'],
})

function getBreadcrumb(pathname, programme) {
  for (const [pattern, crumbs] of Object.entries(breadcrumbsFor(programme))) {
    if (pathname.startsWith(pattern)) return crumbs
  }
  return ['Dashboard']
}

function initials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

export default function TopBar() {
  const location  = useLocation()
  const navigate  = useNavigate()
  const { user, profile, logout } = useAuthStore()
  const { totalWords } = useUIStore()

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)

  const crumbs      = getBreadcrumb(location.pathname, profile?.programme)
  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Scholar'

  useEffect(() => {
    const handler = e => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <header
      className="flex items-center h-14 px-5 shrink-0 gap-4"
      style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        {crumbs.map((crumb, i) => (
          <span key={crumb} className="flex items-center gap-1.5">
            {i > 0 && <span className="font-mono text-xs" style={{ color: '#94a3b8' }}>›</span>}
            <span
              className="font-sans text-sm"
              style={{ color: i === crumbs.length - 1 ? '#0f172a' : '#94a3b8', fontWeight: i === crumbs.length - 1 ? 600 : 400 }}
            >
              {crumb}
            </span>
          </span>
        ))}
      </div>

      {/* Search */}
      <div
        className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', width: 280 }}
      >
        <Search style={{ width: 13, height: 13, color: '#94a3b8', flexShrink: 0 }} />
        <input
          type="text"
          placeholder="Search your documents…"
          className="bg-transparent font-sans text-sm outline-none flex-1 min-w-0 placeholder:text-slate-400"
          style={{ color: '#0f172a' }}
          onFocus={e => e.closest('div').style.borderColor = '#93c5fd'}
          onBlur={e => e.closest('div').style.borderColor = '#e2e8f0'}
        />
      </div>

      {/* Right cluster */}
      <div className="flex items-center gap-2">
        {/* Bell */}
        <button
          className="relative p-2 rounded-lg transition-colors"
          style={{ color: '#94a3b8' }}
          onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <Bell style={{ width: 15, height: 15 }} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: '#2563eb' }} />
        </button>

        {/* Word count */}
        {totalWords > 0 && (
          <div
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
          >
            <span style={{ color: '#2563eb', fontSize: 11 }}>⬡</span>
            <span className="font-mono text-xs font-medium" style={{ color: '#2563eb', fontSize: 11 }}>
              {totalWords.toLocaleString()} words
            </span>
          </div>
        )}

        {/* Avatar dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(v => !v)}
            className="flex items-center gap-1.5 p-1.5 rounded-lg transition-colors"
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center font-sans font-bold text-xs"
              style={{ background: '#2563eb', color: '#ffffff' }}
            >
              {initials(displayName)}
            </div>
            <ChevronDown style={{ width: 12, height: 12, color: '#94a3b8' }} />
          </button>

          {dropdownOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-48 rounded-xl overflow-hidden z-50"
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.12)' }}
            >
              <div className="px-4 py-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                <p className="font-sans text-sm font-semibold truncate" style={{ color: '#0f172a' }}>{displayName}</p>
                <p className="font-sans text-xs truncate mt-0.5" style={{ color: '#94a3b8' }}>{user?.email}</p>
              </div>
              <div className="py-1">
                {[
                  { icon: User,     label: 'Profile',  path: '/app/profile' },
                  { icon: Settings, label: 'Settings', path: '/app/settings' },
                ].map(({ icon: Icon, label, path }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 font-sans text-sm transition-colors"
                    style={{ color: '#475569' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#0f172a' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569' }}
                  >
                    <Icon style={{ width: 13, height: 13 }} /> {label}
                  </Link>
                ))}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 font-sans text-sm transition-colors"
                  style={{ color: '#475569' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569' }}
                >
                  <LogOut style={{ width: 13, height: 13 }} /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
